// Customer Rental Item Actions Configuration
// Actions available to customers for rent-type order items

import { 
  ArrowPathIcon as ReturnIcon, 
  ShoppingCartIcon, 
  EyeIcon, 
  StarIcon, 
  ClockIcon,
  XMarkIcon as XIcon
} from '@heroicons/react/24/outline';

export const customerRentalItemActions = [
  // Cancel Item - Rental (processing + pending)
  {
    id: 'cancel_rental_item',
    label: 'Cancel Item',
    type: 'secondary',
    icon: XIcon,
    conditions: {
      userRole: 'customer',
      entityType: 'orderItem',
      itemType: 'rent',
      fulfillmentGroupStatus: 'processing',
      itemStatus: 'pending',
      timeConstraints: { within24Hours: true },
      multipleItemsInGroup: true
    },
    action: {
      type: 'modal',
      component: 'CancelItemModal',
      mutation: 'useCancelOrderItemMutation',
      api: 'orderItemApi'
    }
  },

  // Cancel Item - Rental (processing + processing)
  {
    id: 'cancel_rental_item_processing',
    label: 'Cancel Item',
    type: 'secondary',
    icon: XIcon,
    conditions: {
      userRole: 'customer',
      entityType: 'orderItem',
      itemType: 'rent',
      fulfillmentGroupStatus: 'processing',
      itemStatus: 'processing',
      timeConstraints: { within24Hours: true },
      multipleItemsInGroup: true
    },
    action: {
      type: 'modal',
      component: 'CancelItemModal',
      mutation: 'useCancelOrderItemMutation',
      api: 'orderItemApi'
    }
  },

  // Keep Item (delivery_completed_to_customer + delivered)
  {
    id: 'keep_rental_item_delivered',
    label: 'Keep Item',
    type: 'primary',
    icon: ShoppingCartIcon,
    conditions: {
      userRole: 'customer',
      entityType: 'orderItem',
      itemType: 'rent',
      fulfillmentGroupStatus: 'delivery_completed_to_customer',
      itemStatus: 'delivered',
      isAvailableForSale: true
    },
    action: {
      type: 'redirect',
      target: '/rental/{itemId}/keep-item-checkout'
    }
  },

  // Extend Rental (delivery_completed_to_customer + delivered)
  {
    id: 'extend_rental_delivered',
    label: 'Extend Rental',
    type: 'secondary',
    icon: ClockIcon,
    conditions: {
      userRole: 'customer',
      entityType: 'orderItem',
      itemType: 'rent',
      fulfillmentGroupStatus: 'delivery_completed_to_customer',
      itemStatus: 'delivered',
      rentalExtensionAvailable: true
    },
    action: {
      type: 'modal',
      component: 'ExtendRentalModal',
      mutation: 'useExtendRentalMutation',
      api: 'orderItemApi'
    }
  },

  // Keep Item (renting_in_progress + in_use)
  {
    id: 'keep_rental_item',
    label: 'Keep Item',
    type: 'primary',
    icon: ShoppingCartIcon,
    conditions: {
      userRole: 'customer',
      entityType: 'orderItem',
      itemType: 'rent',
      fulfillmentGroupStatus: 'renting_in_progress',
      itemStatus: 'in_use',
      isAvailableForSale: true
    },
    action: {
      type: 'modal',
      component: 'KeepItemModal',
      mutation: 'useKeepRentalItemMutation',
      api: 'orderItemApi'
    }
  },

  // Extend Rental (renting_in_progress + in_use)
  {
    id: 'extend_rental',
    label: 'Extend Rental',
    type: 'secondary',
    icon: ClockIcon,
    conditions: {
      userRole: 'customer',
      entityType: 'orderItem',
      itemType: 'rent',
      fulfillmentGroupStatus: 'renting_in_progress',
      itemStatus: 'in_use',
      rentalExtensionAvailable: true
    },
    action: {
      type: 'modal',
      component: 'ExtendRentalModal',
      mutation: 'useExtendRentalMutation',
      api: 'orderItemApi'
    }
  },

  // Start Return (renting_in_progress + in_use)
  {
    id: 'start_rental_return',
    label: 'Start Return',
    type: 'secondary',
    icon: ReturnIcon,
    conditions: {
      userRole: 'customer',
      entityType: 'orderItem',
      itemType: 'rent',
      fulfillmentGroupStatus: 'renting_in_progress',
      itemStatus: 'in_use'
    },
    action: {
      type: 'redirect',
      target: '/dashboard/returns/{itemId}/return-shipping'
    }
  },

  // Keep Item (awaiting_return_shipment + return_pending)
  {
    id: 'keep_rental_item_return_pending',
    label: 'Keep Item',
    type: 'primary',
    icon: ShoppingCartIcon,
    conditions: {
      userRole: 'customer',
      entityType: 'orderItem',
      itemType: 'rent',
      fulfillmentGroupStatus: 'awaiting_return_shipment',
      itemStatus: 'return_pending',
      isAvailableForSale: true
    },
    action: {
      type: 'redirect',
      target: '/rental/{itemId}/keep-item-checkout'
    }
  },

  // Extend Rental (awaiting_return_shipment + return_pending)
  {
    id: 'extend_rental_return_pending',
    label: 'Extend Rental',
    type: 'secondary',
    icon: ClockIcon,
    conditions: {
      userRole: 'customer',
      entityType: 'orderItem',
      itemType: 'rent',
      fulfillmentGroupStatus: 'awaiting_return_shipment',
      itemStatus: 'return_pending',
      rentalExtensionAvailable: true
    },
    action: {
      type: 'modal',
      component: 'ExtendRentalModal',
      mutation: 'useExtendRentalMutation',
      api: 'orderItemApi'
    }
  },

  // Start Return (awaiting_return_shipment + return_pending)
  {
    id: 'start_rental_return_pending',
    label: 'Start Return',
    type: 'secondary',
    icon: ReturnIcon,
    conditions: {
      userRole: 'customer',
      entityType: 'orderItem',
      itemType: 'rent',
      fulfillmentGroupStatus: 'awaiting_return_shipment',
      itemStatus: 'return_pending'
    },
    action: {
      type: 'redirect',
      target: '/dashboard/returns/{itemId}/return-shipping'
    }
  },

  // Track Return Shipment (return_in_transit_to_merchant + return_shipped)
  {
    id: 'track_rental_return_shipment',
    label: 'Track Return Shipment',
    type: 'secondary',
    icon: EyeIcon,
    conditions: {
      userRole: 'customer',
      entityType: 'orderItem',
      itemType: 'rent',
      fulfillmentGroupStatus: 'return_in_transit_to_merchant',
      itemStatus: 'return_shipped',
      hasReturnTracking: true,
      multipleItemsInGroup: true
    },
    action: {
      type: 'modal',
      component: 'TrackingModal',
      query: 'useGetReturnTrackingQuery',
      api: 'orderItemApi'
    }
  },

  // Review Product (return_delivered_to_merchant + return_delivered)
  {
    id: 'review_rental_product',
    label: 'Review Product',
    type: 'secondary',
    icon: StarIcon,
    conditions: {
      userRole: 'customer',
      entityType: 'orderItem',
      itemType: 'rent',
      fulfillmentGroupStatus: 'return_delivered_to_merchant',
      itemStatus: 'return_delivered',
      noExistingReview: true
    },
    action: {
      type: 'redirect',
      target: '/product/{productId}/review'
    }
  }
]; 